import React, {useEffect, useState} from 'react';
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonNote,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {DateTime} from 'luxon';
import {post} from '../../utils/frRequest';
import {useAppDispatch} from '../../lib/hooks';
import {actions} from '../../lib/app';
import {
  useContagemItensCompleta,
  useExecucaoCompleta,
  useExecucaoEmAndamentoPorPendencia,
  useTransferenciasCompletasPendentes,
  usePendenciaCompletaAberta,
  useUserTipo,
  useUsuariosParaSelecao,
} from '../../lib/DataHooks/transferencias';
import {
  TransferenciaCompletaPendente, TransferenciaContagemCompleta, TransferenciaResumo, UsuarioParaSelecao,
} from '../../models/Transferencia';
import SelecionarUsuarioTransferencia from './SelecionarUsuarioTransferencia';
import ConfirmationButton from '../../components/ConfirmationButton';

const RealizarTransferenciaPage: React.FC = () => {
  const {isAdmin, isGestor} = useUserTipo();
  const {pendencia} = usePendenciaCompletaAberta();

  return (
    <div className="p-4">
      {isGestor && pendencia && (
        <IonCard color="warning" className="mb-4">
          <IonCardContent>
            Sua transferência de <strong>{pendencia.de_nome}</strong> para <strong>{pendencia.para_nome}</strong>
            {' '}está aguardando aprovação do admin.
          </IonCardContent>
        </IonCard>
      )}

      <NovaTransferencia/>

      {isAdmin && <TransferenciasCompletasPendentesSecao/>}
    </div>
  );
};

const NovaTransferencia: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [deUid, setDeUid] = useState('');
  const [paraUid, setParaUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [pendente, setPendente] = useState(false);
  const [execucaoId, setExecucaoId] = useState<string | null>(null);
  const [confirmarZeroItens, setConfirmarZeroItens] = useState(false);
  const {usuarios, isLoading: isLoadingUsuarios} = useUsuariosParaSelecao();
  const {
    contagem, isLoading: isLoadingContagem, erro: erroContagem, carregar: carregarContagem,
  } = useContagemItensCompleta();
  const execucao = useExecucaoCompleta(execucaoId);

  const deUsuario = usuarios.find(u => u.uid === deUid) || null;
  const paraUsuario = usuarios.find(u => u.uid === paraUid) || null;

  const validar = () => {
    if (!deUid || !paraUid) return 'Selecione os dois usuários.';
    if (deUid === paraUid) return 'Os usuários DE e PARA devem ser diferentes.';
    return '';
  };

  const handleAvancar = () => {
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }
    setErro('');
    setConfirmarZeroItens(false);
    carregarContagem(deUid);
    setStep(2);
  };

  const handleConfirmar = async () => {
    setErro('');
    setIsLoading(true);

    const res = await post('/veApi/transferencias/completa', {
      de_uid: deUid,
      para_uid: paraUid,
      confirmar_zero_itens: confirmarZeroItens,
    });

    setIsLoading(false);

    if (!res.success) {
      setErro(res.error || 'Ocorreu um erro ao realizar a transferência.');
      return;
    }

    const data = res.data as {status?: string; execucao_id?: string};
    if (data.status === 'aguardando_aprovacao_admin') {
      setPendente(true);
    } else if (data.status === 'em_andamento' && data.execucao_id) {
      setExecucaoId(data.execucao_id);
    }
    setStep(3);
  };

  const handleNovaTransferencia = () => {
    setDeUid('');
    setParaUid('');
    setErro('');
    setPendente(false);
    setExecucaoId(null);
    setConfirmarZeroItens(false);
    setStep(1);
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Nova transferência completa</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {step === 1 && (
          <Passo1
            deUid={deUid}
            paraUid={paraUid}
            usuarios={usuarios}
            isLoadingUsuarios={isLoadingUsuarios}
            onChangeDeUid={setDeUid}
            onChangeParaUid={setParaUid}
            onAvancar={handleAvancar}
            erro={erro}
          />
        )}

        {step === 2 && deUsuario && paraUsuario && (
          <Passo2
            deUsuario={deUsuario}
            paraUsuario={paraUsuario}
            contagem={contagem}
            isLoadingContagem={isLoadingContagem}
            erroContagem={erroContagem}
            confirmarZeroItens={confirmarZeroItens}
            onChangeConfirmarZeroItens={setConfirmarZeroItens}
            onConfirmar={handleConfirmar}
            onVoltar={() => setStep(1)}
            isLoading={isLoading}
            erro={erro}
          />
        )}

        {step === 3 && (
          <Passo3
            pendente={pendente}
            execucaoStatus={execucao.status}
            execucaoResumo={execucao.resumo}
            execucaoErro={execucao.erro}
            execucaoDemorando={execucao.demorando}
            onNovaTransferencia={handleNovaTransferencia}
          />
        )}
      </IonCardContent>
    </IonCard>
  );
};


interface Passo1Props {
  deUid: string;
  paraUid: string;
  usuarios: UsuarioParaSelecao[];
  isLoadingUsuarios: boolean;
  onChangeDeUid: (v: string) => void;
  onChangeParaUid: (v: string) => void;
  onAvancar: () => void;
  erro: string;
}

const Passo1: React.FC<Passo1Props> = ({
  deUid, paraUid, usuarios, isLoadingUsuarios, onChangeDeUid, onChangeParaUid, onAvancar, erro,
}) => (
  <>
    <h2 className="text-lg font-semibold mb-4">Passo 1 — Selecionar usuários</h2>

    <SelecionarUsuarioTransferencia
      label="Usuário DE (origem)"
      usuarios={usuarios}
      isLoading={isLoadingUsuarios}
      value={deUid}
      onChange={onChangeDeUid}
    />

    <div className="mt-2">
      <SelecionarUsuarioTransferencia
        label="Usuário PARA (destino)"
        usuarios={usuarios}
        isLoading={isLoadingUsuarios}
        value={paraUid}
        onChange={onChangeParaUid}
      />
    </div>

    {erro && (
      <IonNote color="danger" className="block mt-2 px-4">
        {erro}
      </IonNote>
    )}

    <div className="mt-4">
      <IonButton expand="block" onClick={onAvancar} disabled={isLoadingUsuarios || usuarios.length === 0}>
        Avançar
      </IonButton>
    </div>
  </>
);


interface Passo2Props {
  deUsuario: UsuarioParaSelecao;
  paraUsuario: UsuarioParaSelecao;
  contagem: TransferenciaContagemCompleta | null;
  isLoadingContagem: boolean;
  erroContagem: string;
  confirmarZeroItens: boolean;
  onChangeConfirmarZeroItens: (v: boolean) => void;
  onConfirmar: () => void;
  onVoltar: () => void;
  isLoading: boolean;
  erro: string;
}

const Passo2: React.FC<Passo2Props> = ({
  deUsuario, paraUsuario, contagem, isLoadingContagem, erroContagem, confirmarZeroItens,
  onChangeConfirmarZeroItens, onConfirmar, onVoltar, isLoading, erro,
}) => {
  const zeroItens = contagem !== null && contagem.total === 0;
  const podeConfirmar = !isLoadingContagem && (!zeroItens || confirmarZeroItens);

  return (
    <>
      <h2 className="text-lg font-semibold mb-4">Passo 2 — Confirmar identidades</h2>

      <div className="grid grid-cols-2 gap-3">
        <UsuarioCard titulo="DE (origem)" usuario={deUsuario} cor="danger"/>
        <UsuarioCard titulo="PARA (destino)" usuario={paraUsuario} cor="primary"/>
      </div>

      <IonCard className="mt-4">
        <IonCardHeader>
          <IonCardTitle className="text-base">Itens que serão transferidos</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {isLoadingContagem && (
            <div className="flex justify-center py-2">
              <IonSpinner/>
            </div>
          )}
          {!isLoadingContagem && erroContagem && (
            <IonNote color="danger" className="block">{erroContagem}</IonNote>
          )}
          {!isLoadingContagem && contagem && (
            <>
              <ResumoLinha label="Fazendas" valor={contagem.fazendas}/>
              <ResumoLinha label="Lojas" valor={contagem.lojas}/>
              <ResumoLinha label="Veterinários" valor={contagem.veterinarios}/>
              <ResumoLinha label="Proprietários" valor={contagem.proprietarios}/>
              <div className="flex justify-between py-1 mt-1 font-semibold">
                <span>Total</span>
                <span>{contagem.total}</span>
              </div>
            </>
          )}
        </IonCardContent>
      </IonCard>

      {zeroItens && (
        <IonCard color="danger" className="mt-4">
          <IonCardContent>
            <strong>Este usuário não possui itens para transferir.</strong> Confirmar apenas desativará a
            conta de <strong>{deUsuario.nome}</strong>, sem mover nenhum dado.
            <IonItem lines="none" className="mt-2">
              <IonCheckbox
                slot="start"
                checked={confirmarZeroItens}
                onIonChange={e => onChangeConfirmarZeroItens(e.detail.checked)}
              />
              <IonLabel className="ion-text-wrap">
                Confirmo que quero desativar {deUsuario.nome} mesmo sem itens para transferir.
              </IonLabel>
            </IonItem>
          </IonCardContent>
        </IonCard>
      )}

      <IonCard color="warning" className="mt-4">
        <IonCardContent>
          <strong>Atenção:</strong> esta operação é irreversível. O usuário DE será desativado após a transferência.
        </IonCardContent>
      </IonCard>

      {erro && (
        <IonNote color="danger" className="block mt-2 px-4">
          {erro}
        </IonNote>
      )}

      <div className="mt-4 flex gap-2">
        {isLoading
          ? <div className="flex justify-center w-full"><IonSpinner/></div>
          : (
            <>
              <IonButton fill="outline" onClick={onVoltar} className="flex-1">
                Voltar
              </IonButton>
              <IonButton color="danger" onClick={onConfirmar} disabled={!podeConfirmar} className="flex-1">
                Confirmar e Transferir
              </IonButton>
            </>
          )}
      </div>
    </>
  );
};

const UsuarioCard: React.FC<{titulo: string; usuario: UsuarioParaSelecao; cor: string}> = ({
  titulo, usuario, cor,
}) => (
  <IonCard>
    <IonCardHeader>
      <IonCardSubtitle>
        <IonText color={cor}>{titulo}</IonText>
      </IonCardSubtitle>
      <IonCardTitle className="text-base">{usuario.nome}</IonCardTitle>
    </IonCardHeader>
    <IonCardContent>
      <p className="text-sm text-gray-600">{usuario.email}</p>
      <p className="text-xs text-gray-400 mt-1">tipo: {usuario.tipo}</p>
    </IonCardContent>
  </IonCard>
);


interface Passo3Props {
  pendente: boolean;
  execucaoStatus: 'em_andamento' | 'concluida' | 'erro' | null;
  execucaoResumo: TransferenciaResumo | null;
  execucaoErro: string;
  execucaoDemorando: boolean;
  onNovaTransferencia: () => void;
}

const Passo3: React.FC<Passo3Props> = ({
  pendente, execucaoStatus, execucaoResumo, execucaoErro, execucaoDemorando, onNovaTransferencia,
}) => (
  <>
    {pendente && (
      <>
        <h2 className="text-lg font-semibold mb-4 text-amber-600">Transferência enviada para aprovação</h2>
        <IonText>
          A transferência foi registrada e está aguardando aprovação de um administrador. Nenhum dado foi
          alterado ainda.
        </IonText>
      </>
    )}

    {!pendente && execucaoStatus === 'em_andamento' && (
      <>
        <h2 className="text-lg font-semibold mb-4">Processando transferência</h2>
        <div className="flex items-center gap-2">
          <IonSpinner/>
          <IonText>
            A transferência está sendo executada em segundo plano. Você pode acompanhar o progresso aqui
            ou voltar mais tarde — ela continua rodando mesmo que você saia desta tela.
          </IonText>
        </div>
        {execucaoDemorando && (
          <IonNote color="warning" className="block mt-2">
            Isso está demorando mais que o esperado. Se persistir, contate o suporte.
          </IonNote>
        )}
      </>
    )}

    {!pendente && execucaoStatus === 'erro' && (
      <>
        <h2 className="text-lg font-semibold mb-4 text-red-600">Erro ao processar a transferência</h2>
        <IonNote color="danger" className="block">{execucaoErro}</IonNote>
      </>
    )}

    {!pendente && execucaoStatus === 'concluida' && (
      <>
        <h2 className="text-lg font-semibold mb-4 text-green-600">Transferência concluída</h2>
        {execucaoResumo && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Resumo</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <ResumoLinha label="Fazendas" valor={execucaoResumo.fazendas}/>
              <ResumoLinha label="Lojas" valor={execucaoResumo.lojas}/>
              <ResumoLinha label="Veterinários" valor={execucaoResumo.veterinarios}/>
              <ResumoLinha label="Proprietários" valor={execucaoResumo.proprietarios}/>
              <ResumoLinha label="Eventos" valor={execucaoResumo.eventos}/>
            </IonCardContent>
          </IonCard>
        )}
      </>
    )}

    <div className="mt-4">
      <IonButton expand="block" onClick={onNovaTransferencia}>
        Nova Transferência
      </IonButton>
    </div>
  </>
);

const ResumoLinha: React.FC<{label: string; valor: number}> = ({label, valor}) => (
  <div className="flex justify-between py-1 border-b border-gray-100">
    <span>{label}</span>
    <span className="font-semibold">{valor}</span>
  </div>
);


const TransferenciasCompletasPendentesSecao: React.FC = () => {
  const {pendencias, isLoading} = useTransferenciasCompletasPendentes();

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-2">Solicitações pendentes</h2>
      {isLoading && (
        <div className="flex justify-center mt-4">
          <IonSpinner/>
        </div>
      )}
      {!isLoading && pendencias.length === 0 && (
        <IonText color="medium">Nenhuma transferência completa pendente.</IonText>
      )}
      {pendencias.map(p => (
        <PendenciaCompletaCard key={p.id} pendencia={p}/>
      ))}
    </div>
  );
};

const PENDENCIA_STATUS_BADGE: Record<string, {label: string; color: string}> = {
  aguardando_aprovacao_admin: {label: 'Aguardando aprovação', color: 'warning'},
  em_andamento: {label: 'Processando', color: 'primary'},
  erro: {label: 'Falhou', color: 'danger'},
};

const PendenciaCompletaCard: React.FC<{pendencia: TransferenciaCompletaPendente}> = ({pendencia}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmarZeroItens, setConfirmarZeroItens] = useState(false);
  const [execucaoId, setExecucaoId] = useState<string | null>(null);
  const {
    contagem, isLoading: isLoadingContagem, erro: erroContagem, carregar: carregarContagem,
  } = useContagemItensCompleta();
  const execucao = useExecucaoCompleta(execucaoId);
  const execucaoRecuperada = useExecucaoEmAndamentoPorPendencia(pendencia.id);

  // 'em_andamento': transferência direta do admin, sem decisão de terceiro a tomar — já nasce
  // processando. 'erro': idem, mas a execução falhou. 'aguardando_aprovacao_admin': fluxo
  // original, com decisão de um gestor a aprovar/cancelar (ver completa-admin-visivel-pendentes).
  const aguardandoDecisao = pendencia.status === 'aguardando_aprovacao_admin';
  const emAndamentoDireto = pendencia.status === 'em_andamento';
  const comErro = pendencia.status === 'erro';

  // recalcula a contagem no momento da revisão, não reaproveita a da abertura da pendência — só
  // faz sentido quando ainda há uma decisão a tomar (transferência direta do admin já foi
  // decidida ao ser disparada, não precisa desse preview)
  useEffect(() => {
    if (aguardandoDecisao) {
      carregarContagem(pendencia.de_uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendencia.de_uid, aguardandoDecisao]);

  // recupera o estado "processando" se o card remontou depois de já ter uma execução em
  // andamento (ex.: usuário trocou de tela e voltou) — sem isso, o card mostraria os botões de
  // Aprovar/Cancelar de novo mesmo com uma aprovação já em curso
  useEffect(() => {
    if (execucaoRecuperada) {
      setExecucaoId(execucaoRecuperada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execucaoRecuperada]);

  // a pendência só vira 'aprovada' (e some da listagem, via onSnapshot) depois que a execução em
  // background concluir com sucesso — em caso de erro ela continua 'aguardando_aprovacao_admin',
  // então liberamos o card pra uma nova tentativa em vez de deixá-lo travado em "processando"
  useEffect(() => {
    if (execucao.status === 'concluida') {
      dispatch(actions.notifySuccess('Transferência aprovada.'));
    } else if (execucao.status === 'erro') {
      dispatch(actions.notifyError(execucao.erro || 'Ocorreu um erro ao processar a transferência.'));
      setExecucaoId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execucao.status]);

  const zeroItens = contagem !== null && contagem.total === 0;
  // transferência direta do admin já está processando por definição (pendencia.status ===
  // 'em_andamento'), mesmo antes da recuperação de execucaoId resolver — não espera o polling
  // confirmar pra decidir a UI, senão os botões de aprovar/cancelar apareceriam por um instante
  const processando = emAndamentoDireto || (execucaoId !== null && execucao.status === 'em_andamento');

  const data = pendencia.criado_em?.toDate
    ? DateTime.fromJSDate(pendencia.criado_em.toDate()).toFormat('dd/MM/yyyy HH:mm')
    : '';

  const handleAprovar = async () => {
    setIsLoading(true);
    const res = await post('/veApi/transferencias/aprovar_completa_pendente', {
      id: pendencia.id,
      confirmar_zero_itens: confirmarZeroItens,
    });
    setIsLoading(false);

    if (!res.success) {
      dispatch(actions.notifyError(res.error || 'Ocorreu um erro ao aprovar a transferência.'));
      return;
    }

    const resData = res.data as {execucao_id?: string};
    if (resData.execucao_id) {
      setExecucaoId(resData.execucao_id);
    }
  };

  const handleCancelar = async () => {
    setIsLoading(true);
    const res = await post('/veApi/transferencias/cancelar_completa_pendente', {id: pendencia.id});
    setIsLoading(false);

    if (!res.success) {
      dispatch(actions.notifyError(res.error || 'Ocorreu um erro ao cancelar a transferência.'));
      return;
    }

    dispatch(actions.notifySuccess('Transferência cancelada.'));
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardSubtitle>{data}</IonCardSubtitle>
        <IonCardTitle className="text-base">
          {pendencia.de_nome} → {pendencia.para_nome}
        </IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <IonBadge color={(PENDENCIA_STATUS_BADGE[pendencia.status] || PENDENCIA_STATUS_BADGE.aguardando_aprovacao_admin).color}>
          {(PENDENCIA_STATUS_BADGE[pendencia.status] || PENDENCIA_STATUS_BADGE.aguardando_aprovacao_admin).label}
        </IonBadge>
        <p className="text-xs text-gray-500 mt-1">
          {pendencia.aberto_por_tipo === 'gestor' ? 'Aberta por gestor' : 'Transferência direta do admin'}
        </p>

        {comErro && (
          <IonNote color="danger" className="block mt-3">
            {pendencia.erro || 'Ocorreu um erro ao processar esta transferência.'}
          </IonNote>
        )}

        {aguardandoDecisao && (
          <>
            <div className="mt-3 text-sm">
              {isLoadingContagem && (
                <div className="flex justify-center py-1">
                  <IonSpinner/>
                </div>
              )}
              {!isLoadingContagem && erroContagem && (
                <IonNote color="danger" className="block">{erroContagem}</IonNote>
              )}
              {!isLoadingContagem && contagem && (
                <p>
                  {contagem.total} item(ns) serão transferidos ({contagem.fazendas} fazenda(s),
                  {' '}{contagem.lojas} loja(s), {contagem.veterinarios} veterinário(s),
                  {' '}{contagem.proprietarios} proprietário(s)).
                </p>
              )}
            </div>

            {zeroItens && (
              <IonCard color="danger" className="mt-2">
                <IonCardContent>
                  <strong>Este usuário não possui itens para transferir.</strong> Aprovar apenas desativará
                  a conta de <strong>{pendencia.de_nome}</strong>.
                  <IonItem lines="none" className="mt-2">
                    <IonCheckbox
                      slot="start"
                      checked={confirmarZeroItens}
                      onIonChange={e => setConfirmarZeroItens(e.detail.checked)}
                    />
                    <IonLabel className="ion-text-wrap">
                      Confirmo que quero desativar {pendencia.de_nome} mesmo sem itens para transferir.
                    </IonLabel>
                  </IonItem>
                </IonCardContent>
              </IonCard>
            )}
          </>
        )}

        {processando && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <IonSpinner/>
            <span>
              Processando {aguardandoDecisao ? 'aprovação' : 'transferência'} em segundo plano...
              {execucao.demorando && ' Está demorando mais que o esperado.'}
            </span>
          </div>
        )}

        {aguardandoDecisao && (
          <div className="mt-4 flex gap-2">
            {processando ? null : isLoading
              ? <div className="flex justify-center w-full"><IonSpinner/></div>
              : (
                <>
                  <ConfirmationButton
                    color="success"
                    className="flex-1"
                    disabled={zeroItens && !confirmarZeroItens}
                    onConfirm={handleAprovar}
                    message="Tem certeza que deseja aprovar esta transferência completa? O usuário DE será desativado após a transferência."
                  >
                    Aprovar
                  </ConfirmationButton>
                  <ConfirmationButton
                    color="danger"
                    fill="outline"
                    className="flex-1"
                    onConfirm={handleCancelar}
                    message="Tem certeza que deseja cancelar esta transferência completa pendente?"
                  >
                    Cancelar
                  </ConfirmationButton>
                </>
              )}
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default RealizarTransferenciaPage;
